import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, ArrowDownRight, Users, Search, Check } from "lucide-react";
import { api, fmtPrice } from "@/lib/api";
import PriceChart from "@/components/PriceChart";
import RiskCalculator from "@/components/RiskCalculator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const INSTRUMENTS = ["EUR/USD","GBP/USD","USD/JPY","AUD/USD","USD/CAD","USD/CHF","NZD/USD"];

export default function AddTrade() {
  const [users, setUsers] = useState([]);
  const [rates, setRates] = useState({});
  const [marketOpen, setMarketOpen] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);   // NEW: multi-select
  const [clientQuery, setClientQuery] = useState("");
  const [form, setForm] = useState({
    instrument: "EUR/USD",
    side: "buy",
    lots: 0.1,
    entry_price: "",
    stop_loss: "",
    take_profit: "",
    comment: "",
    opened_at: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/admin/users").then((r) => setUsers(r.data));
    const load = async () => {
      const { data } = await api.get("/market/rates");
      const map = {};
      data.rates.forEach((r) => (map[r.symbol] = r));
      setRates(map);
      if (data.rates[0]) setMarketOpen(!!data.rates[0].market_open);
    };
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, []);

  const setMarket = () => {
    const r = rates[form.instrument];
    if (!r) return;
    setForm({ ...form, entry_price: form.side === "buy" ? r.ask : r.bid });
  };

  const toggleId = (id) =>
    setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const filteredUsers = useMemo(() => {
    const q = clientQuery.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      u.full_name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    );
  }, [users, clientQuery]);

  const allSelected = filteredUsers.length > 0 && filteredUsers.every((u) => selectedIds.includes(u.id));
  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds((s) => s.filter((id) => !filteredUsers.find((u) => u.id === id)));
    } else {
      const add = filteredUsers.map((u) => u.id).filter((id) => !selectedIds.includes(id));
      setSelectedIds((s) => [...s, ...add]);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (selectedIds.length === 0) return toast.error("Select at least one client");
    if (!form.entry_price) return toast.error("Enter entry price (or use market)");
    setSubmitting(true);
    try {
      const payload = {
        instrument: form.instrument,
        side: form.side,
        lots: parseFloat(form.lots),
        entry_price: parseFloat(form.entry_price),
        stop_loss: form.stop_loss ? parseFloat(form.stop_loss) : null,
        take_profit: form.take_profit ? parseFloat(form.take_profit) : null,
        comment: form.comment,
        opened_at: form.opened_at ? new Date(form.opened_at).toISOString() : null,
      };
      if (selectedIds.length === 1) {
        await api.post("/admin/trades", { ...payload, user_id: selectedIds[0] });
        toast.success(form.opened_at ? "Trade opened (backdated)" : "Trade opened");
      } else {
        const { data } = await api.post("/admin/trades/multi", {
          ...payload,
          user_ids: selectedIds,
        });
        toast.success(`Trade opened in ${data.created} accounts${data.errors?.length ? ` (${data.errors.length} errors)` : ""}`);
      }
      setForm({ ...form, entry_price: "", stop_loss: "", take_profit: "", comment: "", opened_at: "" });
      setSelectedIds([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to open trade");
    } finally {
      setSubmitting(false);
    }
  };

  const r = rates[form.instrument];
  const selectedUsers = users.filter((u) => selectedIds.includes(u.id));
  const totalBalance = selectedUsers.reduce((acc, u) => acc + (u.balance || 0), 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-border" data-testid="add-trade-page">
      <form onSubmit={submit} className="lg:col-span-2 bg-card p-8 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="label-eyebrow mb-2">New Position</p>
            <h3 className="text-lg font-semibold">Open Trade in Client Account(s)</h3>
          </div>
          {!marketOpen && (
            <div
              data-testid="market-closed-banner"
              className="text-[10px] font-semibold uppercase tracking-widest bg-warning/15 text-warning border border-warning/40 rounded px-3 py-1.5"
            >
              Market Closed · Prices frozen
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="label-eyebrow flex items-center gap-1.5">
              <Users className="w-3 h-3" /> Client Accounts
              <span className="text-primary ml-2 font-mono-num normal-case" data-testid="selected-count">
                {selectedIds.length} selected
              </span>
            </Label>
            <button
              type="button"
              onClick={toggleAll}
              className="text-xs text-primary hover:underline font-mono-num"
              data-testid="toggle-all-clients"
            >
              {allSelected ? "Clear filter" : "Select all filtered"}
            </button>
          </div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={clientQuery}
              onChange={(e) => setClientQuery(e.target.value)}
              placeholder="Search by name or email…"
              className="h-10 pl-9 bg-background border-subtle"
              data-testid="client-search"
            />
          </div>
          <div className="mt-2 max-h-64 overflow-y-auto border border-subtle rounded bg-background" data-testid="client-multi-list">
            {filteredUsers.length === 0 && (
              <div className="p-4 text-sm text-muted-foreground text-center">No clients match</div>
            )}
            {filteredUsers.map((u) => {
              const on = selectedIds.includes(u.id);
              return (
                <label
                  key={u.id}
                  className={`flex items-center gap-3 px-3 py-2.5 border-b border-subtle last:border-b-0 cursor-pointer transition-colors ${
                    on ? "bg-primary/10" : "hover:bg-surface-hover"
                  }`}
                  data-testid={`client-row-${u.id}`}
                >
                  <Checkbox checked={on} onCheckedChange={() => toggleId(u.id)} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{u.full_name}</div>
                    <div className="text-xs text-muted-foreground truncate font-mono-num">{u.email}</div>
                  </div>
                  <div className="text-xs font-mono-num text-muted-foreground shrink-0">
                    ${(u.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  {u.blocked && (
                    <span className="text-[9px] font-semibold uppercase text-loss bg-loss/15 px-1.5 py-0.5 rounded">Blocked</span>
                  )}
                </label>
              );
            })}
          </div>
          {selectedIds.length > 1 && (
            <p className="text-xs text-muted-foreground mt-2 font-mono-num" data-testid="multi-summary">
              Trade will be placed in <span className="text-primary font-semibold">{selectedIds.length}</span> accounts (combined balance: ${totalBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}).
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="label-eyebrow">Instrument</Label>
            <Select value={form.instrument} onValueChange={(v) => setForm({ ...form, instrument: v })}>
              <SelectTrigger className="mt-2 bg-background border-subtle h-11" data-testid="trade-instrument-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {INSTRUMENTS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow">Lots</Label>
            <Input type="number" step="0.01" min="0.01" value={form.lots} onChange={(e) => setForm({ ...form, lots: e.target.value })} className="mt-2 bg-background border-subtle h-11 font-mono-num" data-testid="trade-lots" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button type="button" onClick={() => setForm({ ...form, side: "buy" })} className={`h-12 rounded font-semibold transition-colors flex items-center justify-center gap-2 ${form.side === "buy" ? "bg-profit text-white" : "bg-background border border-subtle text-muted-foreground hover:bg-surface-hover"}`} data-testid="trade-side-buy">
            <ArrowUpRight className="w-4 h-4" /> BUY
          </button>
          <button type="button" onClick={() => setForm({ ...form, side: "sell" })} className={`h-12 rounded font-semibold transition-colors flex items-center justify-center gap-2 ${form.side === "sell" ? "bg-loss text-white" : "bg-background border border-subtle text-muted-foreground hover:bg-surface-hover"}`} data-testid="trade-side-sell">
            <ArrowDownRight className="w-4 h-4" /> SELL
          </button>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label className="label-eyebrow">Entry Price</Label>
            <Input type="number" step="0.00001" value={form.entry_price} onChange={(e) => setForm({ ...form, entry_price: e.target.value })} className="mt-2 bg-background border-subtle h-11 font-mono-num" data-testid="trade-entry-price" />
            <button type="button" onClick={setMarket} className="text-xs text-primary hover:underline mt-1.5 font-mono-num" data-testid="use-market-price">Use market price</button>
          </div>
          <div>
            <Label className="label-eyebrow">Stop Loss</Label>
            <Input type="number" step="0.00001" value={form.stop_loss} onChange={(e) => setForm({ ...form, stop_loss: e.target.value })} className="mt-2 bg-background border-subtle h-11 font-mono-num" placeholder="Optional" data-testid="trade-sl" />
          </div>
          <div>
            <Label className="label-eyebrow">Take Profit</Label>
            <Input type="number" step="0.00001" value={form.take_profit} onChange={(e) => setForm({ ...form, take_profit: e.target.value })} className="mt-2 bg-background border-subtle h-11 font-mono-num" placeholder="Optional" data-testid="trade-tp" />
          </div>
        </div>

        <div>
          <Label className="label-eyebrow">Comment</Label>
          <Input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} className="mt-2 bg-background border-subtle h-11" placeholder="Optional note for this trade" data-testid="trade-comment" />
        </div>

        <div>
          <Label className="label-eyebrow">Trade Open Time (Optional — Backdate)</Label>
          <Input
            type="datetime-local"
            value={form.opened_at}
            onChange={(e) => setForm({ ...form, opened_at: e.target.value })}
            className="mt-2 bg-background border-subtle h-11 font-mono-num"
            data-testid="trade-opened-at"
          />
          <p className="text-xs text-muted-foreground mt-1.5">
            Leave blank to open at current server time. Pick a past date/time to record a historical trade.
          </p>
        </div>

        <RiskCalculator
          instrument={form.instrument}
          entryPrice={form.entry_price}
          stopLoss={form.stop_loss}
          clientBalance={selectedUsers[0]?.balance || 0}
          onApplyLots={(lots) => setForm((f) => ({ ...f, lots }))}
        />

        <Button type="submit" disabled={submitting} className="w-full h-11" data-testid="submit-trade">
          {submitting
            ? "Opening…"
            : selectedIds.length > 1
              ? `Open Trade in ${selectedIds.length} Accounts`
              : "Open Trade"}
        </Button>
      </form>

      <aside className="bg-card p-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="label-eyebrow mb-2">Live Quote</p>
            <h3 className="text-lg font-semibold">{form.instrument}</h3>
          </div>
          <div className={`text-[9px] font-semibold uppercase tracking-widest px-2 py-1 rounded ${marketOpen ? "bg-profit/15 text-profit" : "bg-warning/15 text-warning"}`}>
            <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle" style={{ backgroundColor: "currentColor" }} />
            {marketOpen ? "Live" : "Closed"}
          </div>
        </div>
        <PriceChart symbol={form.instrument} height={200} compact />
        {r ? (
          <>
            <div className="grid grid-cols-2 gap-px bg-border">
              <div className="bg-background p-4">
                <div className="label-eyebrow text-loss mb-1">BID (Sell)</div>
                <div className="font-mono-num text-2xl font-semibold text-loss" data-testid="quote-bid">{fmtPrice(form.instrument, r.bid)}</div>
              </div>
              <div className="bg-background p-4">
                <div className="label-eyebrow text-profit mb-1">ASK (Buy)</div>
                <div className="font-mono-num text-2xl font-semibold text-profit" data-testid="quote-ask">{fmtPrice(form.instrument, r.ask)}</div>
              </div>
            </div>
            <div className="text-sm space-y-1.5 text-muted-foreground font-mono-num">
              <div className="flex justify-between"><span>Mid</span><span className="text-foreground">{fmtPrice(form.instrument, r.mid)}</span></div>
              <div className="flex justify-between"><span>Change</span><span className={r.change >= 0 ? "text-profit" : "text-loss"}>{r.change >= 0 ? "+" : ""}{r.change_pct.toFixed(3)}%</span></div>
              <div className="flex justify-between"><span>Spread</span><span className="text-foreground">{((r.ask - r.bid) * (form.instrument === "USD/JPY" ? 100 : 10000)).toFixed(1)} pips</span></div>
            </div>
          </>
        ) : (
          <div className="text-muted-foreground text-sm">Loading quote…</div>
        )}
        <div className="text-xs text-muted-foreground border-t border-subtle pt-4">
          {marketOpen
            ? <>Quotes refresh every 3 seconds. Use &ldquo;Use market price&rdquo; to autofill entry.</>
            : <>Forex market is closed (opens Sunday 21:00 UTC → Friday 21:00 UTC). Prices are frozen until next session.</>
          }
        </div>
      </aside>
    </div>
  );
}
